import { useState } from 'react';
import { Checkbox, FormControlLabel, Popover, Stack, Typography } from '@mui/material';
import { Button } from 'framework/ui/mui-builder';
import type { ColumnVisibilityControl } from './types';

interface ManageColumnsPopoverProps {
  anchorEl: HTMLElement;
  visibility: ColumnVisibilityControl;
  onClose: () => void;
}

const ManageColumnsPopover = ({ anchorEl, visibility, onClose }: ManageColumnsPopoverProps) => {
  const [anchorPosition] = useState(() => {
    const { bottom, left } = anchorEl.getBoundingClientRect();
    return { top: bottom, left };
  });
  const { columns, hiddenColumnIds, defaultHiddenColumnIds = [], onChange } = visibility;
  const canHide = columns.filter(column => !hiddenColumnIds.includes(column.id)).length > 1;
  const isDefaultSet =
    hiddenColumnIds.length === defaultHiddenColumnIds.length &&
    hiddenColumnIds.every(columnId => defaultHiddenColumnIds.includes(columnId));

  const toggleColumn = (columnId: string) => {
    const hidden = hiddenColumnIds.includes(columnId);
    if (!hidden && !canHide) return;
    onChange(hidden ? hiddenColumnIds.filter(item => item !== columnId) : [...hiddenColumnIds, columnId]);
  };

  return (
    <Popover
      open
      anchorReference="anchorPosition"
      anchorPosition={anchorPosition}
      onClose={onClose}
      onClick={event => event.stopPropagation()}
      slotProps={{
        paper: {
          role: 'dialog',
          'aria-label': 'Columns',
          'aria-modal': true,
          tabIndex: -1,
          sx: { mt: 1, minWidth: 240, maxWidth: 360, py: 1 },
        },
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2, pb: 0.5 }}>
        <Typography component="h2" color="textSecondary" fontWeight={600}>
          Columns
        </Typography>
        <Button link color="primary" disabled={isDefaultSet} onClick={() => onChange(defaultHiddenColumnIds)}>
          Reset
        </Button>
      </Stack>
      {columns.map(column => {
        const hidden = hiddenColumnIds.includes(column.id);
        return (
          <FormControlLabel
            key={column.id}
            label={column.label}
            sx={{ display: 'flex', mx: 1, py: 0.5 }}
            control={
              <Checkbox
                size="small"
                checked={!hidden}
                disabled={!hidden && !canHide}
                onChange={() => toggleColumn(column.id)}
                sx={{ p: 0, mr: 1 }}
              />
            }
          />
        );
      })}
    </Popover>
  );
};

export default ManageColumnsPopover;
